"use client";

import { Box, Button, HStack, Text } from "@chakra-ui/react";
import { LuArrowBigDown, LuArrowBigUp } from "react-icons/lu";

type UserVote = "up" | "down" | null;

type CommentUpvoteButtonProps = {
  upCount: number;
  downCount: number;
  userVote: UserVote;
  disabled?: boolean;
  onVote: (vote: Exclude<UserVote, null>) => void;
};

export function CommentVoteButtons({
  upCount,
  downCount,
  userVote,
  disabled = false,
  onVote,
}: CommentUpvoteButtonProps) {
  const isUpSelected = userVote === "up";
  const isDownSelected = userVote === "down";
  const score = upCount - downCount;

  return (
    <Box
      rounded="full"
      bg="whiteAlpha.100"
      _dark={{ bg: "whiteAlpha.200" }}
      p={1}
    >
      <HStack gap={0}>
        <Button
          type="button"
          onClick={() => onVote("up")}
          disabled={disabled}
          aria-label={isUpSelected ? "Remove upvote" : "Upvote this comment"}
          aria-pressed={isUpSelected}
          variant="ghost"
          minW="unset"
          h="18px"
          rounded="full"
          p={{base: 0.5, md:2}}
          color="fg.muted"
          _dark={{ color: "fg.muted" }}
          _hover={{ color: "teal.400", bg: "transparent" }}
          _active={{ bg: "transparent" }}
          size={{base: "2xs", md:"xs"}}
        >
          <LuArrowBigUp fill={isUpSelected ? "currentColor" : "none"} />
        </Button>
        <Text
          minW="18px"
          textAlign="center"
          fontSize="xs"
          fontWeight="semibold"
          fontVariantNumeric="tabular-nums"
          color={score < 0 ? "red.500" : "gray.700"}
          _dark={{ color: score < 0 ? "red.300" : "gray.200" }}
        >
          {score}
        </Text>
        <Button
          type="button"
          onClick={() => onVote("down")}
          disabled={disabled}
          aria-label={isDownSelected ? "Remove downvote" : "Downvote this comment"}
          aria-pressed={isDownSelected}
          variant="ghost"
          minW="unset"
          h="18px"
          rounded="full"
          p={{base: 0.5, md:2}}
          color="fg.muted"
          _dark={{ color: "fg.muted" }}
          _hover={{ color: "teal.400", bg: "transparent" }}
          _active={{ bg: "transparent" }}
          size={{base: "2xs", md:"xs"}}
        >
          <LuArrowBigDown size={8} fill={isDownSelected ? "currentColor" : "none"} />
        </Button>
      </HStack>
    </Box>
  );
}
